import type { TreatmentProcedure } from './treatmentCards'

/**
 * How the drawer groups a patient's outstanding treatment.
 *
 * Ordered by what someone would do about it, which is why "booked and broken"
 * leads: the patient already said yes, so it is the one section that is a phone
 * call rather than a note. Work no plan carries comes last, after all plan work.
 */
export const TREATMENT_SECTIONS = [`broken`, `scheduled`, `open`, `unplanned`] as const

export type TreatmentSection = (typeof TREATMENT_SECTIONS)[number]

/**
 * Where a procedure belongs: what became of it, unless no plan carries it.
 *
 * There is deliberately no "closed plan" section. A procedure is closed only if
 * every plan it sits on is closed, and that does not happen here: inactive plans
 * are superseded revisions that keep pointing at the same live work, so a
 * procedure is virtually always on the active plan as well. Across 400 sampled
 * pending procedures on 57 plans, none were closed-only.
 *
 * `completed` cannot arrive either: the query asks for PENDING.
 */
export function sectionFor(procedure: TreatmentProcedure): TreatmentSection {
  if (procedure.offPlan) return `unplanned`
  return procedure.status === `completed` || procedure.dead ? `open` : procedure.status
}

export function groupBySection(procedures: TreatmentProcedure[]): Map<TreatmentSection, TreatmentProcedure[]> {
  const grouped = new Map<TreatmentSection, TreatmentProcedure[]>()
  for (const procedure of procedures) {
    const key = sectionFor(procedure)
    grouped.set(key, [...(grouped.get(key) ?? []), procedure])
  }
  return grouped
}
